import React from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { ChevronRight } from 'lucide-react'
import { useTar } from '@/modules'
import { CoverArt } from './CoverArt'

interface ResumeStripProps {
  challenges: ChallengeService.AvailableChallenge[]
  onResume: (challenge: ChallengeService.AvailableChallenge) => void
  /** "Show all" — strips every filter and pins the status filter to in-progress. */
  onShowAll: () => void
}

// Two chips fit before the strip crowds the controls row on desktop; the
// narrower mobile row fits only one. The second chip and the "Show all" link are
// CSS-gated by the `md` breakpoint so there's no media-query flash on load.
const MAX_VISIBLE = 2

const RESUMABLE: ChallengeService.AvailableChallengeStatusEnum[] = [
  ChallengeService.AvailableChallengeStatusEnum.Ongoing,
  ChallengeService.AvailableChallengeStatusEnum.Unbooted,
]

// A low-profile shelf rather than a hero band: muted chips on a single
// scrollable row so unfinished work stays reachable without hijacking the
// library view. No accent border, no big cards — it whispers.
export const ResumeStrip = ({ challenges, onResume, onShowAll }: ResumeStripProps) => {
  const { t } = useTar()
  const ongoing = challenges.filter((c) => RESUMABLE.includes(c.status))
  if (ongoing.length === 0) return null
  const visible = ongoing.slice(0, MAX_VISIBLE)
  // "Show all" appears whenever the visible set hides something: 3+ on desktop
  // (second chip shown), 2+ on mobile (only the first chip shown).
  const moreOnDesktop = ongoing.length > MAX_VISIBLE
  const moreOnMobile = ongoing.length > 1

  return (
    <section
      aria-label={t('challenges.resumeHeading')}
      className="flex min-w-0 max-w-full flex-col gap-2"
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
        <span className="font-medium">{t('challenges.resumeHeading')}</span>
      </div>
      <div className="flex min-w-0 gap-2 overflow-x-auto pb-0.5">
        {visible.map((c, i) => (
          <button
            key={c.challengeId ?? c.taskId}
            type="button"
            onClick={() => onResume(c)}
            className={`group flex-none items-center gap-2 rounded-lg border border-border/60 bg-foreground/[0.04] py-1 pe-2.5 ps-1 text-start transition-colors hover:border-accent/60 hover:bg-foreground/[0.07] ${i === 0 ? 'flex' : 'hidden md:flex'}`}
          >
            <CoverArt taskId={c.taskId} coverImageUrl={c.coverImageUrl} focusY={c.coverFocusY} alt="" size="sm" className="h-7 w-7 flex-none rounded-md" />
            <span className="max-w-[12rem] truncate text-xs font-medium text-foreground">{c.name}</span>
            <ChevronRight className="h-3.5 w-3.5 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-accent rtl:rotate-180" />
          </button>
        ))}
        {(moreOnDesktop || moreOnMobile) && (
          <button
            type="button"
            onClick={onShowAll}
            className={`group flex-none items-center gap-1 rounded-lg px-2 text-xs font-medium text-accent transition-colors hover:text-accent/80 ${moreOnDesktop ? 'flex' : 'flex md:hidden'}`}
          >
            <span>{t('challenges.resumeShowAll')}</span>
            <ChevronRight className="h-3.5 w-3.5 flex-none transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
          </button>
        )}
      </div>
    </section>
  )
}
